import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import BlogPostCard from '../components/BlogPostCard';
import BlogFilters from '../components/BlogFilters';
import BlogSearchBar from '../components/BlogSearchBar';
import BlogPagination from '../components/BlogPagination';
import type { WordPressDraftListItem } from '../types/wordpress';
import { getBlogList } from '../services/blogApi';

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const categoryFilter = searchParams.get('category') || '';
  const tagFilter = searchParams.get('tag') || '';
  
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const perPage = 9;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [posts, setPosts] = useState<WordPressDraftListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [categories, setCategories] = useState<{ id: number; name: string; slug: string }[]>([]);
  const [tags, setTags] = useState<{ id: number; name: string; slug: string }[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getBlogList({
          page: currentPage,
          perPage,
          search: searchQuery,
          category: categoryFilter,
          tag: tagFilter,
        });

        setPosts(response.items || []);
        setTotal(response.total || 0);
        setTotalPages(response.total_pages || 1);
        setCategories(response.categories || []);
        setTags(response.tags || []);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Erro ao carregar posts'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, perPage, searchQuery, categoryFilter, tagFilter]);

  const filteredPosts = useMemo(() => posts, [posts]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery) params.set('search', searchQuery);
    else params.delete('search');
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategorySelect = (slug: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('category', slug);
    params.delete('tag');
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleTagSelect = (slug: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tag', slug);
    params.delete('category');
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleClearFilters = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('category');
    params.delete('tag');
    params.set('page', '1');
    setSearchParams(params);
  };

  return (
    <div className="bg-[#081428] text-white min-h-screen">
      <SEO
        title="Blog"
        description="Artigos, notícias e insights sobre inteligência artificial para advogados, legaltech e inovação jurídica."
        keywords="blog, artigos, legaltech, IA para advogados, inteligência artificial jurídica"
        url="/blog"
      />
      
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Blog LawX
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Artigos, notícias e insights sobre inteligência artificial para advogados e inovação jurídica
            </p>
          </div>

          <BlogSearchBar value={searchQuery} onChange={setSearchQuery} onSubmit={handleSearch} />
        </div>
      </section>

      {/* Posts Grid */}
      <section className="pb-16 px-6">
        <div className="container mx-auto max-w-6xl">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
              <span className="ml-3 text-gray-400">Carregando posts...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
              <p className="text-red-400 mb-2">Erro ao carregar posts</p>
              <p className="text-gray-400 text-sm">{error.message}</p>
              <p className="text-gray-500 text-xs mt-4">
                Verifique a configuração do Supabase e as chaves.
              </p>
            </div>
          )}

          {!loading && !error && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                {posts.length === 0 ? (
                  <div className="text-center py-20">
                    <p className="text-gray-400 text-lg mb-2">Nenhum post encontrado</p>
                    <p className="text-gray-500 text-sm">
                      {searchQuery ? 'Tente buscar com outros termos.' : 'Ainda não há posts publicados.'}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                      {filteredPosts.map((post) => (
                        <BlogPostCard key={post.id} post={post} />
                      ))}
                    </div>

                    <BlogPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />

                    <div className="text-center mt-8 text-gray-500 text-sm">
                      Mostrando {filteredPosts.length} de {total} {total === 1 ? 'post' : 'posts'}
                      {searchQuery && ` para "${searchQuery}"`}
                    </div>
                  </>
                )}
              </div>

              <aside className="lg:col-span-1">
                <BlogFilters
                  categories={categories}
                  tags={tags}
                  activeCategory={categoryFilter}
                  activeTag={tagFilter}
                  onSelectCategory={handleCategorySelect}
                  onSelectTag={handleTagSelect}
                  onClear={handleClearFilters}
                />
              </aside>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

